import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Film, Tv, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SiteHeader from './SiteHeader';
import LoginModal from './LoginModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TYPE_LABEL = {
  // The date a title comes out, where nothing says how it comes out. Every
  // connector writes one; the calendar lists it only where the title has no
  // better-typed event on that date.
  RELEASE_DATE_SET: 'Release',
  STREAMING_RELEASE: 'Streaming release',
  SEASON_PREMIERE: 'Season premiere',
  THEATRICAL_RELEASE: 'Theatrical release',
  TRAILER_RELEASED: 'Trailer',
  GAME_LAUNCHED: 'Game launch',
  PLATFORM_RELEASE: 'Platform release',
  EARLY_ACCESS_RELEASED: 'Early access',
  DEMO_RELEASED: 'Demo',
  BETA_STARTED: 'Beta',
  DLC_RELEASED: 'DLC',
  EXPANSION_RELEASED: 'Expansion',
};
const TYPE_STYLE = {
  RELEASE_DATE_SET: 'bg-slate-100 text-slate-700',
  STREAMING_RELEASE: 'bg-green-100 text-green-700',
  SEASON_PREMIERE: 'bg-blue-100 text-blue-700',
  THEATRICAL_RELEASE: 'bg-amber-100 text-amber-700',
  TRAILER_RELEASED: 'bg-rose-100 text-rose-700',
  GAME_LAUNCHED: 'bg-purple-100 text-purple-700',
  PLATFORM_RELEASE: 'bg-purple-100 text-purple-700',
  EARLY_ACCESS_RELEASED: 'bg-indigo-100 text-indigo-700',
  DEMO_RELEASED: 'bg-indigo-100 text-indigo-700',
  BETA_STARTED: 'bg-indigo-100 text-indigo-700',
  DLC_RELEASED: 'bg-teal-100 text-teal-700',
  EXPANSION_RELEASED: 'bg-teal-100 text-teal-700',
};
const OTHER_EVENT_STYLE = 'bg-violet-100 text-violet-700';

// Friendly labels for the broader event timeline; unknown types are formatted
// from their UPPER_SNAKE name.
const EVENT_LABEL = {
  ...TYPE_LABEL,
  // On the timeline the same event means "a date was announced", not "it is
  // out today", so it keeps the fuller label there.
  RELEASE_DATE_SET: 'Release date set',
  PROJECT_ANNOUNCED: 'Announced',
  PROJECT_GREENLIT: 'Greenlit',
  PROJECT_IN_DEVELOPMENT: 'In development',
  PROJECT_ENTERED_PRE_PRODUCTION: 'Pre-production',
  FILMING_STARTED: 'Filming started',
  FILMING_WRAPPED: 'Filming wrapped',
  POST_PRODUCTION_STARTED: 'Post-production',
  CAST_ATTACHED: 'Casting',
  CAST_REMOVED: 'Cast change',
  DIRECTOR_ATTACHED: 'Director attached',
  WRITER_ATTACHED: 'Writer attached',
  STREAMER_ATTACHED: 'Streamer confirmed',
  STUDIO_ATTACHED: 'Studio attached',
  DISTRIBUTOR_ATTACHED: 'Distributor attached',
  EPISODE_RELEASE: 'Episode',
  PROJECT_RELEASED: 'Released',
  PROJECT_CANCELLED: 'Cancelled',
  AWARD_NOMINATION: 'Award nomination',
  AWARD_WIN: 'Award win',
  FUNDING_AWARDED: 'Funding awarded',
};
const eventLabel = (t) => EVENT_LABEL[t]
  || (t || '').toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
// Where a calendar entry points. Prefer the PUBLIC detail route: /insider is
// behind ProtectedRoute, so linking there sends every signed-out visitor into a
// login wall on their first click — which matters a great deal more now the
// calendar is the landing page. Titles carrying a TMDb id have a public page;
// the rest (RSS/TVMaze rows) only exist in Insider, so they still go there.
const eventHref = (event) => {
  if (event.tmdb_id) {
    return `/${event.media_type === 'tv' ? 'tv' : 'movie'}/${event.tmdb_id}`;
  }
  // A game has no TMDb id and never will, so it has its own public page.
  if (event.media_type === 'game' && event.project_id) {
    return `/game/${event.project_id}`;
  }
  return event.project_id
    ? `/insider/project/${event.project_id}`
    : `/?q=${encodeURIComponent(event.person_name || event.title || '')}`;
};

// Curated event-type filter options for the timeline.
// Content-type filter for the month grid. Games joined the calendar with Phase 2
// of the IGDB plan; migration 014 had siloed them onto their own carousel.
// How many genres the select offers, densest first. The catalogue carries about
// 30 in total, so this is the whole list in practice.
const GENRE_CHOICES = 40;

const MEDIA_FILTERS = [
  ['', 'Everything'],
  ['movie', 'Movies'],
  ['tv', 'TV'],
  ['game', 'Games'],
];

const TYPE_FILTERS = [
  ['', 'All events'],
  ['TRAILER_RELEASED', 'Trailers'],
  ['CAST_ATTACHED', 'Casting'],
  ['FILMING_STARTED', 'Filming started'],
  ['FILMING_WRAPPED', 'Filming wrapped'],
  ['THEATRICAL_RELEASE', 'Theatrical'],
  ['STREAMING_RELEASE', 'Streaming'],
  ['AWARD_NOMINATION', 'Awards'],
  ['FUNDING_AWARDED', 'Funding'],
  ['PROJECT_CANCELLED', 'Cancelled'],
];

const monthKey = (d) => d.slice(0, 7);
// A "YYYY-MM-DD" string as a LOCAL date. new Date(d) parses it as UTC midnight,
// which is the previous day anywhere west of Greenwich — so a US reader saw the
// wrong day on every label and the wrong month on every group heading.
const localDate = (d) => {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day || 1);
};
const monthLabel = (key) => localDate(key + '-01').toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
const dayLabel = (d) => localDate(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const todayKey = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('en-CA');
};

// Shift a "YYYY-MM-DD" key by whole days. Built from the parts rather than from
// Date(d) so it stays on local dates: parsing a bare YYYY-MM-DD gives UTC
// midnight, which is the previous day for anyone west of Greenwich.
const shiftDay = (d, delta) => {
  const shifted = localDate(d);
  shifted.setDate(shifted.getDate() + delta);
  return shifted.toLocaleDateString('en-CA');
};

// The day-bounded views. The day view is one day and pages with the arrows, so
// it needs the cursor; "This week" is the next seven days including today, not
// the rest of the calendar week — on a Saturday the calendar-week reading
// leaves the view holding one day, which is not what someone asking "what is
// out this week" wants.
//
// "Weekend" is the coming Friday to Sunday; once Friday has arrived it starts
// today, so Saturday does not show a Friday that has already gone. "Quarter" is
// today to the last day of the current calendar quarter.
const weekendWindow = () => {
  const dow = new Date().getDay();
  const toSunday = (7 - dow) % 7;
  const fromFriday = [5, 6, 0].includes(dow) ? 0 : toSunday - 2;
  return [addDays(fromFriday), addDays(toSunday)];
};
const quarterWindow = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
  return [todayKey(), end.toLocaleDateString('en-CA')];
};
const viewWindow = (view, day) => {
  if (view === 'day') return [day, day];
  if (view === 'week') return [todayKey(), addDays(6)];
  if (view === 'weekend') return weekendWindow();
  if (view === 'quarter') return quarterWindow();
  return null;
};

// Heading for a single day. "Today" and "Tomorrow" read better than the date,
// and they are the two days these views exist for.
const dayHeading = (d) => {
  if (d === todayKey()) return 'Today';
  if (d === addDays(1)) return 'Tomorrow';
  return localDate(d).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
};

// Shift a "YYYY-MM" key by whole months.
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// A 7-column matrix of date keys (or null pads) for a "YYYY-MM" month.
const monthMatrix = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const startWeekday = new Date(y, m - 1, 1).getDay();
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = Array(startWeekday).fill(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(`${ym}-${String(d).padStart(2, '0')}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export default function Calendar() {
  const { token, isAuthenticated } = useAuth();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // "all" = every upcoming release (public); "following" = only titles the user
  // follows (requires login).
  const [scope, setScope] = useState('all');
  const [showLogin, setShowLogin] = useState(false);
  // "day" = one day, paged with the arrows and opening on today; "week" = the
  // next seven days; "month" = month grid (default); "list" = grouped agenda
  // that doubles as the timeline (all event types, past + future), by type.
  const [view, setView] = useState('month');
  const [cursor, setCursor] = useState(() => todayKey().slice(0, 7)); // YYYY-MM shown in month view
  const [dayCursor, setDayCursor] = useState(todayKey); // YYYY-MM-DD shown in day view
  const [typeFilter, setTypeFilter] = useState(''); // event_type filter for the list/timeline
  const [mediaFilter, setMediaFilter] = useState(''); // '' = every medium
  const [genreFilter, setGenreFilter] = useState(''); // '' = every genre
  const [sortBy, setSortBy] = useState(''); // '' = date then title; 'popularity' = most popular first in a day
  const [genres, setGenres] = useState([]);

  // The genre list comes from the hub endpoint, which is snapshot-cached
  // server-side and ordered densest first. Hard-coding TMDb's list here would
  // go stale, and would name genres this catalogue does not carry.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/hubs/genres?limit=${GENRE_CHOICES}`);
        if (r.ok && !cancelled) setGenres(((await r.json()).results || []).map((g) => g.name));
      } catch {
        // A missing genre list only costs the filter, not the calendar.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Only the month grid refetches when the cursor moves. Keeping the raw cursor
  // in load()'s dependencies would refetch the list view on every month change
  // too, for a URL that does not use it.
  const monthParam = view === 'month' && scope !== 'watchlist' ? cursor : null;

  const load = useCallback(async () => {
    setIsLoading(true);
    let url;
    if (scope === 'watchlist') {
      url = `${API_BASE}/api/watchlist/watchlist-feed?days=365`;
    } else if (view === 'list') {
      // The list doubles as the timeline: all event types, past + future.
      url = `${API_BASE}/api/insider/events?days_back=90&days_ahead=365${typeFilter ? `&event_type=${typeFilter}` : ''}`;
    } else if (viewWindow(view, dayCursor)) {
      // Day and This week ask for their own window. The limit is the endpoint's
      // maximum: September holds 847 events, so a week can carry ~200 and a
      // single day ~30.
      const [from, to] = viewWindow(view, dayCursor);
      url = `${API_BASE}/api/insider/calendar?from_date=${from}&to_date=${to}&limit=300`
            + (mediaFilter ? `&media_type=${mediaFilter}` : '')
            + (genreFilter ? `&genre=${encodeURIComponent(genreFilter)}` : '')
            + (sortBy ? `&sort=${sortBy}` : '');
    } else {
      // The month grid asks for the month it is showing. It used to fill from
      // /calendar?days=365, which was fine while the whole year held ~110
      // events; the catalogue now carries thousands, so one windowed request
      // ran out inside the first week and later months rendered empty.
      const [year, month] = monthParam.split('-');
      const params = new URLSearchParams();
      if (mediaFilter) params.set('media_type', mediaFilter);
      if (genreFilter) params.set('genre', genreFilter);
      if (sortBy) params.set('sort', sortBy);
      url = `${API_BASE}/api/insider/calendar/month/${Number(year)}/${Number(month)}`
            + (params.toString() ? `?${params}` : '');
    }
    try {
      const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (r.ok) setEvents((await r.json()).events || []);
      else setEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, scope, view, typeFilter, monthParam, mediaFilter, genreFilter, sortBy, dayCursor]);

  useEffect(() => { load(); }, [load]);

  // Opening a day from the month grid. The grid is a lookup tool and a cell
  // shows three titles at most, so the day view is where a busy date is
  // actually readable.
  const openDay = (d) => {
    setDayCursor(d);
    setView('day');
  };

  // The list view is one window over many months, so jump it to the first month
  // that has anything. The month grid fetches its own month and must not move
  // itself, or paging forward would snap straight back.
  useEffect(() => {
    if (view === 'list' && events.length) setCursor(monthKey(events[0].event_date));
  }, [view, events]);

  // The watchlist feed is one year-long request whatever the view, so a day
  // window has to be applied here as well as in the URL.
  const dayWindow = viewWindow(view, dayCursor);
  const visible = dayWindow
    ? events.filter((e) => e.event_date >= dayWindow[0] && e.event_date <= dayWindow[1])
    : events;

  // Group events for the agenda views: by day where the view is a day window,
  // by month for the timeline. The feed isn't strictly date-ordered, so sort
  // ascending first.
  const groupKey = (d) => (dayWindow ? d : monthKey(d));
  const groupHeading = (k) => (dayWindow ? dayHeading(k) : monthLabel(k));
  const groups = [];
  let current = null;
  for (const e of [...visible].sort((a, b) => a.event_date.localeCompare(b.event_date))) {
    const key = groupKey(e.event_date);
    if (!current || current.key !== key) {
      current = { key, items: [] };
      groups.push(current);
    }
    current.items.push(e);
  }

  // The legend follows what is on screen. Listing every known type would put
  // seven game badges above a film-only month, which reads as a promise the
  // grid does not keep.
  const legendTypes = Object.keys(TYPE_STYLE).filter(
    (t) => visible.some((e) => e.event_type === t)
  );

  // Group events by exact date for the month grid.
  const byDate = {};
  for (const e of visible) (byDate[e.event_date] = byDate[e.event_date] || []).push(e);

  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <CalendarDays className="w-6 h-6 text-videbits-600" /> Release Calendar
        </h1>
        <p className="text-sm text-gray-500 mb-6">Upcoming streaming releases, season premieres, theatrical openings and trailers.</p>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            {[['all', 'All releases'], ['watchlist', 'Watchlist']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                // "Watchlist" is personalised, so prompt sign in when signed out.
                onClick={() => (key === 'watchlist' && !isAuthenticated ? setShowLogin(true) : setScope(key))}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  scope === key ? 'bg-videbits-600 text-white' : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            {[['day', 'Day'], ['week', 'This week'], ['weekend', 'Weekend'], ['month', 'Month'], ['quarter', 'Quarter'], ['list', 'List']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  view === key ? 'bg-videbits-600 text-white' : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {view !== 'list' && scope === 'all' && (
            <select
              value={mediaFilter}
              onChange={(e) => {
                setMediaFilter(e.target.value);
                // A game carries no genre, so keeping one would empty the view.
                if (e.target.value === 'game') setGenreFilter('');
              }}
              aria-label="Content type"
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700"
            >
              {MEDIA_FILTERS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
          )}
          {/* Games carry no genre rows — the IGDB connector reads genres but
              nothing writes project_genres — so the filter is hidden on the
              games view rather than offered and returning nothing. */}
          {view !== 'list' && scope === 'all' && mediaFilter !== 'game' && genres.length > 0 && (
            <select
              value={genreFilter}
              onChange={(e) => setGenreFilter(e.target.value)}
              aria-label="Genre"
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700"
            >
              <option value="">All genres</option>
              {genres.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          )}
          {view !== 'list' && scope === 'all' && (
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Sort"
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700"
            >
              <option value="">By title</option>
              <option value="popularity">Most popular first</option>
            </select>
          )}
          {view === 'list' && scope === 'all' && (
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700"
            >
              {TYPE_FILTERS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
          )}
        </div>

        {/* Colour legend — filled chips matching the event badges/cells. */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {legendTypes.map((t) => (
            <span key={t} className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${TYPE_STYLE[t]}`}>
              {TYPE_LABEL[t]}
            </span>
          ))}
          {view === 'list' && (
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${OTHER_EVENT_STYLE}`}>
              Other events
            </span>
          )}
        </div>

        {/* The pagers sit above the content, not inside it. Kept inside, an
            empty day or month rendered the empty state alone and stranded the
            reader on it with nothing to page with. */}
        {view === 'day' && scope === 'all' && (
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={() => setDayCursor((d) => shiftDay(d, -1))} aria-label="Previous day"
                    className="p-2 rounded-lg text-videbits-600 hover:bg-videbits-50 hover:text-videbits-800">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{dayHeading(dayCursor)}</h2>
              {dayCursor !== todayKey() && (
                <button type="button" onClick={() => setDayCursor(todayKey())}
                        className="text-xs font-medium text-videbits-600 hover:text-videbits-800">
                  Back to today
                </button>
              )}
            </div>
            <button type="button" onClick={() => setDayCursor((d) => shiftDay(d, 1))} aria-label="Next day"
                    className="p-2 rounded-lg text-videbits-600 hover:bg-videbits-50 hover:text-videbits-800">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
        {view === 'month' && (
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={() => setCursor((c) => shiftMonth(c, -1))} aria-label="Previous month"
                    className="p-2 rounded-lg text-videbits-600 hover:bg-videbits-50 hover:text-videbits-800">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{monthLabel(cursor)}</h2>
            <button type="button" onClick={() => setCursor((c) => shiftMonth(c, 1))} aria-label="Next month"
                    className="p-2 rounded-lg text-videbits-600 hover:bg-videbits-50 hover:text-videbits-800">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {isLoading ? (
          <p className="text-gray-400 text-sm">Loading…</p>
        ) : groups.length === 0 ? (
          <p className="text-gray-400 text-sm">
            {scope === 'watchlist'
              ? 'No upcoming releases from titles in your watchlist yet. Add a title to your watchlist to see it here.'
              : view === 'day'
                ? (dayCursor === todayKey() ? 'Nothing is out today.' : 'Nothing is out on this day.')
                : view === 'week'
                  ? 'Nothing is out in the next seven days.'
                  : view === 'weekend'
                    ? 'Nothing is out this weekend.'
                    : view === 'quarter'
                      ? 'Nothing is out before the end of this quarter.'
                      : view === 'list'
                    ? 'No events in this window.'
                    : 'No upcoming dated releases yet.'}
          </p>
        ) : view === 'month' ? (
          <div>
            <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-xl overflow-hidden">
              {WEEKDAYS.map((w) => (
                <div key={w} className="bg-gray-50 text-center text-[11px] font-medium text-gray-500 py-2">{w}</div>
              ))}
              {monthMatrix(cursor).map((d, i) => (
                <div key={d || `pad-${i}`}
                     className={`relative bg-white min-h-[84px] p-1.5 ${d ? 'hover:bg-gray-50' : ''} ${d === todayKey() ? 'ring-2 ring-inset ring-videbits-400' : ''}`}>
                  {d && (
                    <>
                      {/* A cell holds at most three of a day that can carry
                          thirty, so the cell itself has to open the day. The
                          button covers the cell and sits under the titles: a
                          click on a title still goes to that title, a click on
                          anything else opens the day. */}
                      <button
                        type="button"
                        onClick={() => openDay(d)}
                        aria-label={dayHeading(d)}
                        className="absolute inset-0 w-full h-full cursor-pointer"
                      />
                      <div className="relative pointer-events-none">
                        <div className="text-[11px] text-gray-400 mb-1">{Number(d.slice(8))}</div>
                        <div className="space-y-1">
                          {(byDate[d] || []).slice(0, 3).map((e, j) => (
                            <Link
                              key={`${e.project_id}:${e.event_type}:${j}`}
                              to={eventHref(e)}
                              title={`${e.title} — ${TYPE_LABEL[e.event_type] || e.event_type}`}
                              className={`pointer-events-auto block truncate rounded px-1 py-0.5 text-[11px] font-medium ${TYPE_STYLE[e.event_type] || OTHER_EVENT_STYLE}`}
                            >
                              {e.title}
                            </Link>
                          ))}
                          {(byDate[d] || []).length > 3 && (
                            <button
                              type="button"
                              onClick={() => openDay(d)}
                              className="pointer-events-auto block text-[10px] text-gray-400 px-1 hover:text-videbits-600"
                            >
                              +{byDate[d].length - 3} more
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.key} className="mb-8">
              {view !== 'day' && (
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{groupHeading(g.key)}</h2>
              )}
              <div className="space-y-2">
                {g.items.map((e, i) => (
                  <Link
                    key={`${e.project_id}:${e.event_type}:${e.event_date}:${i}`}
                    to={eventHref(e)}
                    className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-4 py-3 hover:shadow-sm transition-shadow"
                  >
                    {/* The day-window views already carry the date in the group
                        heading, so the date block would only repeat it. */}
                    {!dayWindow && (
                      <div className="w-12 text-center flex-shrink-0">
                        <div className="text-xs text-gray-400">{dayLabel(e.event_date).split(' ')[1]}</div>
                        <div className="text-lg font-semibold text-gray-900 leading-none">{dayLabel(e.event_date).split(' ')[0]}</div>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                        {e.person_id
                          ? <User className="w-3.5 h-3.5 text-videbits-500" />
                          : e.media_type === 'tv'
                            ? <Tv className="w-3.5 h-3.5 text-gray-400" />
                            : <Film className="w-3.5 h-3.5 text-gray-400" />}
                        {e.title}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${TYPE_STYLE[e.event_type] || OTHER_EVENT_STYLE}`}>
                      {eventLabel(e.event_type)}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} initialMode="login" />
    </div>
  );
}
